import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  MoreHorizontal, FileText, Braces, Trash2, Pencil, Search, MessageSquarePlus,
  Zap, BookOpen, CalendarClock, Package, Calendar, FolderKanban, Layers, Sun, Moon, LogOut, Building2,
  SlidersHorizontal, PanelLeftClose, PanelLeftOpen, Check, ChevronUp, ChevronDown, Brain,
} from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Aster } from "@/components/brand/Aster";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useChatContext } from "./providers/ChatProvider";
import { useAppearance } from "@/theme/AppearanceProvider";
import { useCurrentUser } from "@/auth/RequireAuth";
import { useCompany } from "@/hooks/useCompany";
import { useOrgs } from "@/hooks/useOrgs";
import { useAuthedApi } from "@/api/useAuthedApi";
import { AVATAR_CHANGED_EVENT } from "@/api/profileApi";
import { listProjects } from "../../api/projects";
import { groupByRecency } from "./sessionGrouping";
import { EmbedLayoutToggle } from "@/embed/EmbedLayoutToggle";
import { ConfirmDialog } from "../Common/ConfirmDialog";
import { SettingsModal } from "../Preferences/SettingsModal";
import { MemoryDialog } from "../Preferences/MemoryDialog";
import { COMMAND_MENU_OPEN_EVENT } from "./CommandMenu";
import { NEW_CHAT_EVENT } from "./EmptyChatWelcome";
import { IconButton, Menu, MenuItem, MenuDivider, MenuHeader, Tooltip } from "@/components/ui";
import { cn } from "@/lib/cn";
import { downloadBlob } from "@/lib/download";
import type { Session } from "../../chat/types";
import { useConversationKnowledgeEnabled } from "./ConversationKnowledge/useConversationKnowledgeEnabled";
import { ConversationKnowledgeMenuItems } from "./ConversationKnowledge/ConversationKnowledgeMenuItems";
import { SaveConversationDialog } from "./ConversationKnowledge/SaveConversationDialog";

// Workspace nav. "New Chat" routes home AND starts a fresh chat. Dashboard is
// intentionally absent (route stays alive at /workspace/dashboard — spec §Nav).
// `ph` is the item's analytics tag, written out so the coverage scanner can
// check it (tags are never built from data, spec T5). New Chat starts a chat;
// every other item is a destination, named by its area.
function navItems(projectsEnabled: boolean): { href: string; label: string; icon: React.ReactNode; ph: string }[] {
  return [
    { href: "/workspace", label: "New Chat", icon: <MessageSquarePlus size={17} />, ph: "chat-create" },
    ...(projectsEnabled
      ? [{ href: "/workspace/projects", label: "Projects", icon: <FolderKanban size={17} />, ph: "projects" }]
      : []),
    { href: "/workspace/skills", label: "Skills", icon: <Zap size={17} />, ph: "skills" },
    { href: "/workspace/knowledge", label: "Knowledge", icon: <BookOpen size={17} />, ph: "knowledge" },
    { href: "/workspace/meetings", label: "Meetings", icon: <CalendarClock size={17} />, ph: "meetings" },
    { href: "/workspace/plugins", label: "Plugins", icon: <Package size={17} />, ph: "plugins" },
    { href: "/workspace/schedules", label: "Schedules", icon: <Calendar size={17} />, ph: "schedules" },
    { href: "/workspace/batches", label: "Batches", icon: <Layers size={17} />, ph: "batches" },
  ];
}

/** Clean a session title for display. Backend auto-titling occasionally stores
 *  a raw first-message payload instead of a short title — e.g. a serialized
 *  tool_result block (`[{"type":"tool_result", ... }]`) — which rendered as a
 *  wall of escaped JSON in the sidebar. Collapse whitespace to one line and, if
 *  the title still looks like a serialized JSON/tool blob, drop it so the
 *  "Untitled chat" fallback shows instead. Length is clipped in CSS (truncate).*/
function cleanTitle(raw: string | null | undefined): string {
  const t = (raw ?? "").replace(/\s+/g, " ").trim();
  if (!t) return "";
  // JSON-ish blob (starts with [{ or {" ) or contains tool-payload markers →
  // not a human title.
  if (/^[[{]/.test(t) || /"(type|tool_result|artifact_id|content)"\s*:/.test(t)) {
    return "";
  }
  return t;
}

/** Page ink vs sidebar-scoped ink (embed hosts often brand the sidebar dark). */
function sidebarTone(embedded: boolean) {
  if (!embedded) {
    return {
      ink: "text-ink",
      secondary: "text-ink-secondary",
      muted: "text-ink-muted",
      faint: "text-ink-faint",
      navIcon: "text-nav-icon",
      hover: "hover:bg-hover",
      surfaceHover: "hover:bg-hover focus-visible:bg-hover",
      navActive: "bg-nav-active",
      iconBtn: "text-ink-secondary",
      skeleton: "bg-hover",
    } as const;
  }
  return {
    ink: "text-sidebar-ink",
    secondary: "text-sidebar-ink-secondary",
    muted: "text-sidebar-ink-muted",
    faint: "text-sidebar-ink-faint",
    navIcon: "text-sidebar-nav-icon",
      hover: "hover:bg-sidebar-hover",
      surfaceHover: "hover:bg-sidebar-hover focus-visible:bg-sidebar-hover",
      navActive: "bg-sidebar-nav-active",
      iconBtn: "text-sidebar-ink-secondary",
      skeleton: "bg-sidebar-hover",
  } as const;
}

/** Below 900px the permanent sidebar would crowd out the conversation, so it
 *  becomes a temporary overlay drawer toggled from the chat header. */
function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 899px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 899px)");
    const onChange = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return mobile;
}

/** End-of-list sentinel for the paged session list: auto-loads the next page
 *  when scrolled into view; click works as a manual fallback. Appending never
 *  moves the viewport, so no scroll anchoring is needed here. */
function ShowMoreSessions({
  loadMore,
  mutedClass = "text-ink-muted",
}: {
  loadMore: () => Promise<void>;
  mutedClass?: string;
}) {
  const ref = React.useRef<HTMLButtonElement | null>(null);
  const loadingRef = React.useRef(false);
  const trigger = React.useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      await loadMore();
    } finally {
      loadingRef.current = false;
    }
  }, [loadMore]);
  React.useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void trigger();
      },
      { rootMargin: "120px" },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [trigger]);
  return (
    <button
      ref={ref}
      type="button"
      data-ph="expand_more"
      onClick={() => void trigger()}
      className={cn("px-1 py-2 text-left text-[12px] hover:underline", mutedClass)}
    >
      Show more chats…
    </button>
  );
}

const COLLAPSED_KEY = "egpt.sidebarCollapsed";
const RECENTS_COLLAPSED_KEY = "egpt.recentsCollapsed";

/** Read/write a boolean collapse preference without letting storage failures
 *  reach the UI. Inside a cross-origin embed, storage partitioning makes both
 *  calls throw a SecurityError — which used to escape the click handler.
 *  Shared by the sidebar-width collapse and the Recent-chats collapse. */
function readCollapsed(key: string): boolean {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeCollapsed(key: string, next: boolean): void {
  try {
    localStorage.setItem(key, next ? "1" : "0");
  } catch {
    /* storage blocked — the choice just won't survive a reload */
  }
}

const PROJECT_SECTIONS_COLLAPSED_KEY = "egpt.collapsedProjectIds";

/** Each project section folds independently (unlike Recent chats, there can
 *  be several), so the preference is a set of ids rather than one flag. */
function readCollapsedIdSet(key: string): Set<string> {
  try {
    const raw = JSON.parse(localStorage.getItem(key) ?? "[]");
    return Array.isArray(raw) ? new Set(raw.filter((x): x is string => typeof x === "string")) : new Set();
  } catch {
    return new Set();
  }
}

function writeCollapsedIdSet(key: string, ids: Set<string>): void {
  try {
    localStorage.setItem(key, JSON.stringify(Array.from(ids)));
  } catch {
    /* storage blocked — the choice just won't survive a reload */
  }
}

/** Uppercase section label + chevron — "Recent chats" and each per-project
 *  bucket share this so every chat section (not just Recent chats) folds
 *  independently. Click anywhere on the row to toggle. */
function SectionHeader({
  label,
  collapsed,
  onToggle,
}: {
  label: string;
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      data-ph={collapsed ? "section-expand" : "section-collapse"}
      onClick={onToggle}
      aria-expanded={!collapsed}
      className="flex w-full items-center gap-1 rounded-md px-1 pt-3 pb-1 text-left cursor-pointer hover:text-ink"
    >
      <span className="flex-1 min-w-0 truncate text-[10.5px] font-bold uppercase tracking-[0.08em] text-ink-muted">
        {label}
      </span>
      <ChevronDown
        size={13}
        className={cn(
          "shrink-0 text-ink-faint transition-transform duration-150",
          !collapsed && "rotate-180",
        )}
      />
    </button>
  );
}

/** Account avatar: the user's photo with an initials fallback. Renders the
 *  photo over the initials; if there's no avatar the image 404s and the
 *  initials stay. Re-attempts the image on AVATAR_CHANGED_EVENT. */
function AccountAvatar({ initials }: { initials: string }) {
  const { profile } = useAuthedApi();
  const [version, setVersion] = useState(0);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const onChange = () => { setFailed(false); setVersion((v) => v + 1); };
    window.addEventListener(AVATAR_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(AVATAR_CHANGED_EVENT, onChange);
  }, []);
  return (
    <span className="relative h-8 w-8 shrink-0 rounded-md bg-accent text-white grid place-items-center text-xs font-bold overflow-hidden">
      {initials || "U"}
      {!failed && (
        <img
          src={profile.avatarHref(version)}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      )}
    </span>
  );
}

export interface SessionSidebarProps {
  /** Mobile drawer open state. When the viewport is below the desktop
   *  breakpoint the sidebar renders inside a temporary overlay drawer driven
   *  by these props; on desktop they're ignored and it renders inline. */
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  /** Embed: restrict the workspace nav to these href keys (last path segment,
   *  e.g. "skills","knowledge","plugins","schedules"). undefined = show all. */
  tabsFilter?: string[];
  /** Rich embed: render as a permanent inline column even below the mobile
   *  breakpoint. Inside a fixed-width iframe `window.matchMedia` reports the
   *  iframe width, so a narrow embed would otherwise self-hide as a closed
   *  overlay drawer (mobileOpen defaults false, no toggle) — the "sidebar not
   *  showing" bug. When set, skip the drawer path and always render inline. */
  embedded?: boolean;
  /** Embed: switch back to the compact top-bar layout (classic sidebar only). */
  onToggleEmbedLayout?: () => void;
  /** Embed: which layout is active — drives the toggle tooltip. */
  embedLayout?: "compact" | "classic";
}

export function SessionSidebar({
  mobileOpen = false,
  onMobileClose,
  tabsFilter,
  embedded = false,
  onToggleEmbedLayout,
  embedLayout = "classic",
}: SessionSidebarProps = {}) {
  const { sessions, sessionId, setSessionId, slots, client, features } = useChatContext();
  // One const filter, not a reassigned `let`: the coverage scanner trusts
  // `item.ph` only when the mapped list traces back to navItems' literals.
  const want = tabsFilter?.length ? new Set(tabsFilter.map((t) => t.toLowerCase())) : null;
  const NAV_ITEMS = navItems(features.projects).filter((it) => {
    if (!want) return true;
    const key = it.href.split("/").filter(Boolean).pop() || it.href;
    return want.has(key.toLowerCase()) || it.href === "/"; // always keep New Chat
  });
  const pathname = useLocation().pathname;
  const navigate = useNavigate();
  const { mode, toggleMode } = useAppearance();
  const user = useCurrentUser();
  const company = useCompany();
  // The ACTIVE org (server-flagged via /v1/orgs) — NOT /v1/companies/me, which
  // can lag behind the org switcher. The footer subtitle and the menu
  // checkmark must reflect the org the session is actually scoped to.
  const { orgs, activeOrg } = useOrgs();
  const { companies } = useAuthedApi();
  const activeOrgName = activeOrg?.name ?? company?.name ?? null;
  // Menu rows: all memberships when /v1/orgs answered; otherwise fall back to
  // the single company summary so the menu never goes blank mid-load.
  const menuOrgs = orgs.length
    ? orgs
    : company
      ? [{ company_id: company.id, name: company.name, roles: [], joined_at: null, active: true }]
      : [];
  const [orgSwitching, setOrgSwitching] = useState(false);
  const onSwitchOrg = async (companyId: string) => {
    if (orgSwitching) return;
    setOrgSwitching(true);
    try {
      await companies.switchOrg(companyId);
      // Full reload: sessions/knowledge/etc. are fetched per active org —
      // same contract as the org-settings page switcher.
      window.location.reload();
    } catch {
      setOrgSwitching(false);
    }
  };
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountBtnRef = useRef<HTMLButtonElement>(null);
  const displayName = user?.name?.trim() || user?.email || "Account";
  const initials = displayName.split(/[\s@.]+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0]!.toUpperCase()).join("");
  const isMobile = useIsMobile();
  // Drawer slide: matched to the inline column's 200ms width transition so the
  // two collapse gestures feel like the same mechanism. Instant when the user
  // prefers reduced motion.
  const reduceMotion = useReducedMotion() ?? false;
  const drawerTransition = reduceMotion
    ? { duration: 0 }
    : { duration: 0.2, ease: [0.4, 0, 0.2, 1] as const };

  const onLogout = async () => {
    const { logout } = await import("@/auth/useAuth");
    await logout(import.meta.env.VITE_API_URL ?? "");
    navigate("/login", { replace: true });
  };
  // Per-row anchor for the export-menu popover. Null when closed; otherwise
  // the row id whose menu is open. Anchor element lives in component-local
  // state so the Menu attaches to the right kebab button.
  const [menuFor, setMenuFor] = useState<{ id: string; anchor: HTMLElement } | null>(
    null,
  );
  const [projectNames, setProjectNames] = useState<Record<string, string>>({});
  // Inline-rename: the row id currently being edited (null = none) + its draft.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  // Conversations as knowledge (spec §7.3): the menu items show only while
  // the backend feature is on; saveFor is the conversation being saved.
  const conversationKnowledgeEnabled = useConversationKnowledgeEnabled();
  const [saveFor, setSaveFor] = useState<string | null>(null);
  // Pending delete target for the in-page confirm dialog (null = closed).
  const [pendingDelete, setPendingDelete] = useState<{ id: string; title: string } | null>(null);
  // Collapsible sidebar (Claude-style): collapsed = thin icon rail (nav icons
  // only, chat list hidden); expanded = full 280px column. Persisted so the
  // choice sticks across reloads — fail-soft, because an embed in a
  // storage-partitioned iframe throws on localStorage and the toggle itself
  // must keep working (persistence is the nicety, not the feature).
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed(COLLAPSED_KEY));
  const setCollapsedPersist = (next: boolean) => {
    setCollapsed(next);
    writeCollapsed(COLLAPSED_KEY, next);
  };
  // Recent chats is its own collapsible section — independent of the
  // sidebar-width collapse above — so a long chat history doesn't dominate
  // the panel. Defaults open (no behaviour change until you fold it).
  const [recentsCollapsed, setRecentsCollapsed] = useState<boolean>(() =>
    readCollapsed(RECENTS_COLLAPSED_KEY),
  );
  const toggleRecentsCollapsed = () => {
    const next = !recentsCollapsed;
    setRecentsCollapsed(next);
    writeCollapsed(RECENTS_COLLAPSED_KEY, next);
  };
  // Each project section folds independently of Recent chats and of each
  // other — same idea, but keyed by project id since there can be several.
  const [collapsedProjectIds, setCollapsedProjectIds] = useState<Set<string>>(() =>
    readCollapsedIdSet(PROJECT_SECTIONS_COLLAPSED_KEY),
  );
  const toggleProjectCollapsed = (pid: string) => {
    setCollapsedProjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      writeCollapsedIdSet(PROJECT_SECTIONS_COLLAPSED_KEY, next);
      return next;
    });
  };
  // Layout treats a rich embed as desktop: it renders as a permanent inline
  // column (with the collapse rail), never the full-width mobile drawer, even
  // though the iframe width trips the mobile media query.
  const layoutMobile = isMobile && !embedded;
  // On mobile the drawer always shows full content (no thin icon rail).
  const expanded = layoutMobile || !collapsed;
  const toggleCollapsed = () => setCollapsedPersist(!collapsed);
  const tone = sidebarTone(embedded);

  useEffect(() => {
    let cancelled = false;
    listProjects()
      .then((ps) => { if (!cancelled) setProjectNames(Object.fromEntries(ps.map((p) => [p.id, p.name]))); })
      .catch(() => {/* degrade */});
    return () => { cancelled = true; };
  }, []);

  // Sessions split by project: ungrouped ones are the chronological "Recent
  // chats" list; each project's own sessions get their own section below so
  // a chat is findable by "when" (recent) or by "what it's for" (project),
  // never both at once.
  const groups = useMemo(() => {
    const byProject = new Map<string | null, Session[]>();
    for (const s of sessions.sessions) {
      const key = s.project_id ?? null;
      const list = byProject.get(key) ?? [];
      list.push(s);
      byProject.set(key, list);
    }
    return byProject;
  }, [sessions.sessions]);

  const recentSessions = useMemo(
    () => groupByRecency(groups.get(null) ?? [], Date.now()).flatMap((b) => b.sessions),
    [groups],
  );

  const projectKeys = useMemo(
    () => Array.from(groups.keys()).filter((k): k is string => k !== null),
    [groups],
  );

  const closeMenu = () => setMenuFor(null);

  const exportSession = async (format: "md" | "json") => {
    if (!menuFor) return;
    const target = menuFor.id;
    closeMenu();
    try {
      const { filename, body, mime } = await client.exportSession(target, format);
      downloadBlob(filename, body, mime);
    } catch (e) {
      // Soft fail — the kebab is a convenience action, not load-bearing.
      // A future iteration could surface a toast; today the dev console
      // is fine.
      // eslint-disable-next-line no-console
      console.warn("export failed", e);
    }
  };

  // Step 1: the kebab "Delete chat" item stashes the target and opens an
  // in-page confirm dialog (no native window.confirm).
  const requestDelete = () => {
    if (!menuFor) return;
    const target = menuFor.id;
    const title =
      sessions.sessions.find((s) => s.id === target)?.title?.trim() || "this chat";
    closeMenu();
    setPendingDelete({ id: target, title });
  };

  // Step 2: the dialog's confirm actually removes it. Throws bubble up to the
  // dialog, which surfaces them inline.
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const target = pendingDelete.id;
    await sessions.remove(target);
    setPendingDelete(null);
    // If we just deleted the chat we were viewing, drop to a fresh chat so the
    // main pane doesn't sit on a now-gone session.
    if (target === sessionId) {
      setSessionId(null);
      navigate("/workspace");
    }
  };

  const startRename = (id: string, currentTitle: string) => {
    closeMenu();
    setRenamingId(id);
    setRenameDraft(currentTitle);
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameDraft("");
  };

  const commitRename = async (id: string) => {
    const next = renameDraft.trim();
    const current = sessions.sessions.find((s) => s.id === id)?.title?.trim() ?? "";
    cancelRename();
    // No-op on empty or unchanged titles — don't clobber a good auto-title.
    if (!next || next === current) return;
    try {
      await sessions.rename(id, next);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("rename failed", e);
    }
  };

  const renameFromMenu = () => {
    if (!menuFor) return;
    const target = menuFor.id;
    const current =
      sessions.sessions.find((s) => s.id === target)?.title?.trim() || "";
    startRename(target, current);
  };

  const renderRow = (s: Session) => {
    // Backend rows occasionally come back with empty / whitespace titles
    // (auto-titler hasn't filled them in yet). Without a fallback the row
    // would render as visually empty whitespace.
    const cleaned = cleanTitle(s.title);
    const title = cleaned || "Untitled chat";
    const isRenaming = renamingId === s.id;
    const selected = s.id === sessionId;
    return (
      <div
        key={s.id}
        role="button"
        tabIndex={0}
        data-ph={isRenaming ? undefined : "open"}
        onClick={() => {
          if (isRenaming) return;
          setSessionId(s.id);
          // Route to /workspace so the chat surface (the <Outlet/> child) renders.
          // The ChatProvider is persistent in WorkspaceLayout, so setSessionId
          // above already takes effect; carrying the id in the URL (`?session=`)
          // keeps the URL authoritative — ChatSurface re-applies it on mount and
          // it survives browser back/forward. On /workspace already, no navigation
          // is needed.
          if (pathname !== "/workspace") {
            navigate(`/workspace?session=${encodeURIComponent(s.id)}`);
          }
          onMobileClose?.();
        }}
        onKeyDown={(e) => {
          if (isRenaming) return;
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); (e.currentTarget as HTMLElement).click(); }
        }}
        onDoubleClick={(e) => { e.stopPropagation(); startRename(s.id, title === "Untitled chat" ? "" : title); }}
        className={cn(
          "group flex items-center gap-1 rounded-md px-2.5 py-1 cursor-pointer",
          "text-[13px] transition-colors border",
          selected
            ? cn(tone.navActive, "border-transparent", tone.ink, "font-medium")
            : cn("border-transparent", tone.secondary, tone.hover),
        )}
      >
        {isRenaming ? (
          <input
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            data-ph="rename"
            onClick={(e) => e.stopPropagation()}
            onBlur={() => commitRename(s.id)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") { e.preventDefault(); commitRename(s.id); }
              else if (e.key === "Escape") { e.preventDefault(); cancelRename(); }
            }}
            autoFocus
            placeholder="Chat name"
            aria-label="rename chat"
            className={cn("flex-1 min-w-0 bg-transparent border-b border-accent outline-none text-[13px]", tone.ink)}
          />
        ) : (
          <span className={cn("flex-1 min-w-0 truncate", !cleaned && tone.faint)}>
            {title}
          </span>
        )}
        {!isRenaming && (
          <IconButton
            size="sm"
            label={`actions for ${title}`}
            data-ph="actions-open"
            className={cn(
              tone.iconBtn,
              "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 shrink-0",
              selected && "opacity-100",
            )}
            onClick={(e) => { e.stopPropagation(); setMenuFor({ id: s.id, anchor: e.currentTarget }); }}
          >
            <MoreHorizontal size={14} />
          </IconButton>
        )}
      </div>
    );
  };

  const body = (
    <aside
      data-ph-scope="sp"
      className={cn(
        "flex h-full min-h-0 flex-col shrink-0 overflow-hidden bg-sidebar font-sans",
        "border-r border-edge transition-[width] duration-200",
      )}
      style={{ width: layoutMobile ? 300 : expanded ? 280 : 64 }}
    >
      {/* Header: logo tile + wordmark (SPA only) + chrome controls */}
      <div
        className={cn(
          "flex items-center pt-4 pb-2",
          expanded ? cn("px-4", embedded ? "justify-end" : "justify-between") : "px-2 justify-center",
        )}
      >
        {expanded && !embedded && (
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="h-[34px] w-[34px] shrink-0 grid place-items-center">
              <Aster variant="onSidebar" size={30} />
            </div>
            <div className="leading-none min-w-0">
              <div className={cn("text-[15px] font-bold truncate", tone.ink)}>Artie</div>
            </div>
          </div>
        )}
        <div className="flex items-center gap-0.5 shrink-0">
          {embedded && onToggleEmbedLayout && (
            expanded ? (
              <EmbedLayoutToggle
                layout={embedLayout}
                onToggle={onToggleEmbedLayout}
                variant="labeled"
                tone="sidebar"
              />
            ) : (
              <EmbedLayoutToggle
                layout={embedLayout}
                onToggle={onToggleEmbedLayout}
                variant="icon"
                tone="sidebar"
              />
            )
          )}
          {expanded && (
            <Tooltip title="Search (⌘K)" placement="right">
              <IconButton
                size="sm"
                label="open search"
                data-ph="search-open"
                className={tone.iconBtn}
                onClick={() => window.dispatchEvent(new Event(COMMAND_MENU_OPEN_EVENT))}
              >
                <Search size={17} />
              </IconButton>
            </Tooltip>
          )}
          {layoutMobile ? (
            // Drawer: there is no rail to collapse into (the drawer always shows
            // full content), so the toggle would only flip its own icon and
            // nothing else. Dismissing the drawer is the real action here.
            <Tooltip title="Close menu" placement="right">
              <IconButton size="sm" label="close navigation" data-ph="close" className={tone.iconBtn} onClick={onMobileClose}>
                <PanelLeftClose size={17} />
              </IconButton>
            </Tooltip>
          ) : !expanded ? (
            embedded ? (
              <Tooltip title="Expand sidebar" placement="right">
                <IconButton size="sm" label="expand sidebar" data-ph="expand" className={tone.iconBtn} onClick={toggleCollapsed}>
                  <PanelLeftOpen size={17} />
                </IconButton>
              </Tooltip>
            ) : (
              // Collapsed rail: the Aster mark IS the expand control. It shows the
              // logo at rest and cross-fades to the expand icon on hover/focus.
              <Tooltip title="Expand sidebar" placement="right">
                <button
                  type="button"
                  aria-label="expand sidebar"
                  data-ph="expand"
                  onClick={toggleCollapsed}
                  className={cn("group relative grid h-9 w-9 place-items-center rounded-[10px] focus-visible:outline-none", tone.surfaceHover)}
                >
                  <span className="col-start-1 row-start-1 transition-opacity duration-150 group-hover:opacity-0 group-focus-visible:opacity-0">
                    <Aster variant="onSidebar" size={26} />
                  </span>
                  <span className={cn("col-start-1 row-start-1 flex opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100", tone.secondary)}>
                    <PanelLeftOpen size={17} />
                  </span>
                </button>
              </Tooltip>
            )
          ) : (
            <Tooltip title="Collapse sidebar" placement="right">
              <IconButton size="sm" label="collapse sidebar" data-ph="collapse" className={tone.iconBtn} onClick={toggleCollapsed}>
                <PanelLeftClose size={17} />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Nav */}
      <nav className={cn("flex flex-col gap-0.5", expanded ? "px-3" : "px-2 items-center")}>
        {NAV_ITEMS.map((item) => {
          const selected = item.href === "/workspace" ? pathname === "/workspace" : pathname.startsWith(item.href);
          const row = (
            <Link
              key={item.href}
              to={item.href}
              data-ph={item.ph}
              onClick={() => {
                if (item.href === "/workspace") {
                  setSessionId(null);
                  // Spin the new-chat Aster even if we're already on /workspace
                  // (no remount would otherwise fire the mount animation).
                  window.dispatchEvent(new Event(NEW_CHAT_EVENT));
                }
                onMobileClose?.();
              }}
              className={cn(
                "flex items-center gap-3 rounded-md py-2 text-[13.5px] no-underline",
                expanded ? "px-3" : "px-0 w-9 justify-center",
                selected
                  ? cn(tone.navActive, tone.ink, "font-semibold")
                  : cn(tone.secondary, "font-medium", tone.hover),
              )}
            >
              <span className={cn(tone.navIcon, "shrink-0")}>{item.icon}</span>
              {expanded && <span className="truncate">{item.label}</span>}
            </Link>
          );
          return expanded ? row : (
            <Tooltip key={item.href} title={item.label} placement="right">{row}</Tooltip>
          );
        })}
      </nav>

      {expanded && slots.sidebarTop /* Tier 3 #12 render slot — Schedules */}

      {/* Recents */}
      {expanded ? (
        <div data-ph-scope="recents" className="flex-1 min-h-0 overflow-y-auto px-3 py-2">
          {sessions.loading && sessions.sessions.length === 0 && (
            <div className="px-1 py-2 space-y-2" aria-busy="true" aria-label="Loading chats">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className={cn("h-7 rounded-lg animate-pulse", tone.skeleton)} />
              ))}
            </div>
          )}
          {sessions.sessions.length === 0 && !sessions.loading && (
            <p className={cn("px-1 py-4 text-center text-xs", tone.faint)}>No chats yet</p>
          )}
          {/* Ungrouped sessions (no project_id), most-recent-first */}
          {recentSessions.length > 0 && (
            <>
              <div className={cn("px-1 pt-3 pb-1 text-[10.5px] font-bold uppercase tracking-[0.08em]", tone.muted)}>
                Recent chats
              </div>
              {recentSessions.map(renderRow)}
              {/* Paged list: server may hold older sessions beyond the loaded
                  window. Scrolling the sentinel into view auto-loads the next
                  page; the button doubles as a manual fallback. */}
              {sessions.hasMore && (
                <ShowMoreSessions loadMore={sessions.loadMore} />
              )}
            </>
          )}
          {/* Paged list: server may hold older sessions beyond the loaded
              window. Scrolling the sentinel into view auto-loads the next
              page; the button doubles as a manual fallback. */}
          {sessions.hasMore && (
            <ShowMoreSessions loadMore={sessions.loadMore} mutedClass={tone.muted} />
          )}
          {/* Project buckets rendered after the ungrouped ones */}
          {projectKeys.map((pid) => (
            <React.Fragment key={pid}>
              <div className={cn("px-1 pt-3 pb-1 text-[10.5px] font-bold uppercase tracking-[0.08em]", tone.muted)}>
                {projectNames[pid] ?? "Project"}
              </div>
              {(groups.get(pid) ?? []).map(renderRow)}
            </React.Fragment>
          ))}
        </div>
      ) : (
        /* When collapsed (no chat list), a spacer keeps the footer at bottom. */
        <div className="flex-1" />
      )}

      {/* Kebab menu (rename / export / delete) */}
      <Menu anchorEl={menuFor?.anchor ?? null} open={!!menuFor} onClose={closeMenu} scope="sp-recents-menu">
        <MenuItem icon={<Pencil size={14} />} data-ph="rename" onClick={renameFromMenu}>Rename</MenuItem>
        <MenuDivider />
        <MenuItem icon={<FileText size={14} />} data-ph="export_md" onClick={() => exportSession("md")}>Export as markdown</MenuItem>
        <MenuItem icon={<Braces size={14} />} data-ph="export_json" onClick={() => exportSession("json")}>Export as JSON</MenuItem>
        {conversationKnowledgeEnabled && menuFor && (
          <ConversationKnowledgeMenuItems
            sessionId={menuFor.id}
            onSave={() => {
              setSaveFor(menuFor.id);
              closeMenu();
            }}
          />
        )}
        <MenuDivider />
        <MenuItem icon={<Trash2 size={14} />} data-ph="delete" danger onClick={requestDelete}>Delete chat</MenuItem>
      </Menu>

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete this chat?"
        body={<><strong>{pendingDelete?.title}</strong> will be permanently deleted. This can't be undone.</>}
        confirmLabel="Delete"
        destructive
        onConfirm={confirmDelete}
        onClose={() => setPendingDelete(null)}
        scope="sp-recents-delete_dialog"
      />
      {slots.sidebarBottom /* Tier 3 #13 Scheduled tasks render slot */}

      {/* Footer: account trigger + popup. Hidden in the embed — the host app
          already shows the signed-in user, the embed has no real identity of
          its own (no RequireAuth context, so it rendered a placeholder
          "Account"), and the menu's Log out (→/login) and Members dead-end on
          routes the embed never mounts. */}
      {!embedded && (
      <div className={cn("p-2.5", !expanded && "flex justify-center")}>
        <button
          ref={accountBtnRef}
          type="button"
          onClick={() => setAccountOpen(true)}
          aria-haspopup="menu"
          aria-expanded={accountOpen}
          data-ph="account"
          className={cn(
            "flex items-center gap-2.5 rounded-md cursor-pointer transition-colors w-full text-left",
            // Expanded: a neutral grey chip (bg-popover) matching the menu.
            // Collapsed: just the avatar — no surface, border, or shadow.
            expanded
              ? "bg-popover border border-hairline shadow-sm hover:bg-hover px-2.5 py-2"
              : "p-1 w-auto hover:bg-hover",
          )}
        >
          <AccountAvatar initials={initials} />
          {expanded && (
            <>
              <span className="flex-1 min-w-0">
                <span className="block text-[13px] font-semibold text-ink truncate">{displayName}</span>
                {activeOrgName && <span className="block text-[11.5px] text-ink-muted truncate">{activeOrgName}</span>}
              </span>
              <ChevronUp size={14} className="text-ink-faint shrink-0" />
            </>
          )}
        </button>
        <Menu anchorEl={accountBtnRef.current} open={accountOpen} onClose={() => setAccountOpen(false)} vertical="top" scope="sp-account">
          {user?.email && <MenuHeader>{user.email}</MenuHeader>}
          {menuOrgs.length > 0 && (
            <>
              {menuOrgs.map((o) => (
                <button
                  key={o.company_id}
                  type="button"
                  role="menuitemradio"
                  data-ph="org-select"
                  aria-checked={o.active}
                  aria-label={o.active ? `${o.name || "Untitled organization"} (active)` : `switch to ${o.name || "Untitled organization"}`}
                  disabled={orgSwitching}
                  onClick={() => { if (!o.active) void onSwitchOrg(o.company_id); }}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left transition-colors",
                    o.active ? "bg-hover cursor-default" : "cursor-pointer hover:bg-hover",
                  )}
                >
                  <span className="h-6 w-6 rounded-md bg-accent text-white grid place-items-center text-[10px] font-bold shrink-0">
                    {(o.name || "?").slice(0, 2).toUpperCase()}
                  </span>
                  <span
                    className={cn(
                      "flex-1 min-w-0 text-[13.5px] truncate",
                      o.active ? "font-semibold text-ink" : "font-medium text-ink-secondary",
                      !o.name && "italic text-ink-muted",
                    )}
                  >
                    {o.name || "Untitled organization"}
                  </span>
                  {o.active && <Check size={15} className="text-accent shrink-0" />}
                </button>
              ))}
              <MenuDivider />
            </>
          )}
          <MenuItem
            icon={<SlidersHorizontal size={15} />}
            data-ph="settings"
            onClick={() => {
              setAccountOpen(false);
              setSettingsOpen(true);
            }}
          >
            Settings
          </MenuItem>
          {/* Memory sits here, one click from the account menu, rather than
              nested inside Settings — it is something users need to be able to
              find, not hunt for. */}
          <MenuItem
            icon={<Brain size={15} />}
            data-ph="memory"
            onClick={() => {
              setAccountOpen(false);
              setMemoryOpen(true);
            }}
          >
            Memory
          </MenuItem>
          <MenuItem
            icon={<Building2 size={15} />}
            data-ph="organization"
            onClick={() => {
              setAccountOpen(false);
              onMobileClose?.();
              navigate("/workspace/organization");
            }}
          >
            Organization Settings
          </MenuItem>
          <MenuItem
            icon={mode === "light" ? <Moon size={15} /> : <Sun size={15} />}
            data-ph="theme-toggle"
            onClick={() => { toggleMode(); setAccountOpen(false); }}
          >
            {mode === "light" ? "Switch to dark theme" : "Switch to light theme"}
          </MenuItem>
          <MenuDivider />
          <MenuItem icon={<LogOut size={15} />} data-ph="logout" danger onClick={() => { setAccountOpen(false); onLogout(); }}>
            Log out
          </MenuItem>
        </Menu>
        <SettingsModal
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
        />
        <MemoryDialog open={memoryOpen} onClose={() => setMemoryOpen(false)} />
      </div>
      )}
    </aside>
  );

  // Mobile: render the body in a temporary overlay drawer toggled from the
  // chat header. Desktop: render inline as a permanent column. Rich embed:
  // always inline (the embed explicitly asked for the sidebar and sized its
  // own iframe — never hide it behind a drawer that has no trigger).
  // Outside the drawer: a save outlives the drawer closing, and its toast too.
  const saveDialog = (
    <SaveConversationDialog sessionId={saveFor} onClose={() => setSaveFor(null)} />
  );
  if (isMobile && !embedded) {
    // The drawer slides in and out with its scrim (the inline column animates
    // its width instead). AnimatePresence keeps it mounted for the exit, so
    // dismissing it reads as a movement rather than a disappearance.
    return (
      <>
        <AnimatePresence>
          {mobileOpen && (
            <div
              className="fixed inset-0 z-40"
              role="dialog"
              aria-modal="true"
              aria-label="navigation"
              onKeyDown={(e) => { if (e.key === "Escape") onMobileClose?.(); }}
            >
              <motion.div
                className="absolute inset-0 bg-black/40"
                // The scrim sits outside the sidebar's `sp` scope, so it names it.
                data-ph="sp-close_backdrop"
                onClick={onMobileClose}
                aria-hidden
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={drawerTransition}
              />
              <motion.div
                data-testid="nav-drawer-panel"
                className="absolute inset-y-0 left-0 shadow-2xl"
                initial={{ x: "-100%" }}
                animate={{ x: 0 }}
                exit={{ x: "-100%" }}
                transition={drawerTransition}
              >
                {body}
              </motion.div>
            </div>
          )}
        </AnimatePresence>
        {saveDialog}
      </>
    );
  }
  return (
    <>
      {body}
      {saveDialog}
    </>
  );
}
